// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-01-27 17:16:12 +0100 (Tue, 27 Jan 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 160064 $
//  Purpose     : uix root sagas
// =============================================================================
// ---Third-party imports
import { buffers, channel, END } from 'redux-saga';
import { delay, call, put, takeEvery, take, all, select as sselect, putResolve, actionChannel, fork, spawn, race } from 'redux-saga/effects';
export { delay, buffers };
import now from 'performance-now';
import queryString from 'query-string';
// ---Framework imports
import { uix, Sagas, Actions, Sel, Views } from 'uix/registries';
import { mergeI18nLayers } from 'uix/i18n/utils';
import Api, { fetch } from 'uix/api';
import log, { loggedExceptions } from 'uix/logging';
import { decodeRequest, ensurePathPrefix } from 'uix/routing';
import { selectInitCacheKeys, getAncestorDomains } from 'uix/selectors';
import { hashObj, timeouts, isSSR, setCustomVital } from 'uix/utils';
import { checkAcl } from 'uix/auth/acl';
import { getDeployConfig, pathPrefixIntercept } from 'uix/deploy';
import { get, items, filterItems, fromPairs, mapItems, merge, size, has, dict, keys, isNil, range, isEqual, strip, uniq, sorted, values, flatten } from 'ldash';
import { getErrorEnvDetails } from 'uix/utils/error';
// ---Local imports
import { GENERATOR } from 'uix/actions';
// XXX: chunking aspect breaks SSR
import { loadCodeForDomains } from 'uix/dcode';
// load module chunk before api-init
import * as modloader from 'uix/loadmodules';
// load saga chunks before api-init
import * as sagaloader from './load';
import { registerLazySagas, getCriticalFailures } from './lazy_modules';
// ---Globals
log.info('setup core sagas');
registerLazySagas();
function* watchInitDomainSSR() {
    // SSR can dispatch INIT_DOMAIN before lazy sagas are registered (r159989 moved
    // the watcher to sagas/extra). Actions are not replayed, so SET_DOMAIN_DATA
    // never fires and tests time out. Keep this SSR-only watcher to ensure domain
    // init always runs in SSR.
    const chan = yield actionChannel(Actions.initDomain, buffers.expanding(100));
    while(true){
        let action = yield take(chan);
        yield initializeDomain(action.payload.dname);
    }
}
export const matchServerEvent = (eventName)=>({ type, meta })=>type === 'SERVER/EVENT' && meta.eventName === eventName;
// added lodash.get(path, default) shortcut for string selectors
export function select(selector, ...args) {
    if (!selector) {
        return sselect();
    } else if (typeof selector === 'function') {
        return sselect(selector, ...args);
    } else if (typeof selector === 'string') {
        return sselect((root)=>get(root, selector, ...args));
    } else log.error('wrong selector type %o', selector);
}
export function* rehydrateApp(action) {
    let url = get(action, 'payload.url');
    if (url) {
        yield put(Actions.setAppState({
            state: 'INIT_APP',
            apiurl
        }));
    } else {
        // vldeploy
        yield put(Actions.setAppState({
            state: 'INIT_APP',
            apiurl: null
        }));
    }
}
export let recoveredCachedConfigs = (ddataCache, ddata)=>{
    let dname = ddata.name;
    let oldCfg = ddataCache[dname];
    if (!oldCfg) {
        return ddata;
    }
    let recovered = {};
    let logMsg = [];
    for (let key of [
        'types',
        'dconfig'
    ]){
        if (ddata.cacheKeys[key] && !ddata[key]) {
            if (ddata.cacheKeys[key] != oldCfg.cacheKeys[key]) {
                log.error('bad cache for %o', key);
            } else {
                logMsg.push(key);
                //~ log.debug('recovered %o config %o', key, ddata.cacheKeys[key])
                recovered[key] = oldCfg[key];
            }
        }
    }
    let recovered_i18n = {};
    for (let language of keys(ddata.i18n)){
        let key = `i18n_${language}`;
        if (ddata.cacheKeys[key] && !ddata.i18n[language]) {
            if (ddata.cacheKeys[key] != oldCfg.cacheKeys[key]) {
                log.error('bad cache for %o', key);
            } else {
                //~ log.debug('recovered %o config %o', key, ddata.cacheKeys[key])
                logMsg.push(key);
                recovered_i18n[language] = oldCfg.i18n[language];
            }
        }
    }
    if (logMsg.length > 0) {
        log.debug('from cache: %o', logMsg.join(', '));
    }
    return {
        ...ddata,
        ...recovered,
        i18n: {
            ...ddata.i18n,
            ...recovered_i18n
        }
    };
};
function resolveDomainData(ddata, user, instance) {
    let { name: dname } = ddata;
    ddata = {
        ...ddata,
        ...mergeUser(user, dname)
    };
    let { typesById } = instance;
    ddata = resolveActiveDescendantTypes(ddata, typesById);
    // XXX: also resolve activeDescendantStates
    return ddata;
}
export function* initializeApp({ payload }) {
    /* main initialization saga
    Initializes the application by handling deployment configuration, setting up
    internationalization, user and domain context, loading necessary modules and
    handling routing based on the provided 'deploy' info in payload.

    It performs the following actions:
    - Aligns logging system with persisted UI state. (disabled)
    - Merges deployment configuration from the payload with the existing one.
    - Intercepts the path prefix for routing purposes.
    - Checks if the application cache is initialized and retrieves necessary keys.
    - Forks a non-blocking saga to load module chunks in parallel with API initialization.
    - Calls the API to initialize the app with the current pathname and cache keys.
    - Waits for module code to be loaded before proceeding.
    - Processes the received configuration data, dispatches actions to set up the app state,
      user data, domain information, client configurations, and UI state.
    - Decodes the request parameters to determine if auth is needed and sets up filters.
    - Sets the application state to running and activates the view for the user.
    - On error, dispatches an action to represent the failing state.
 */ // align logging system once with persisted ui state
    //~ yield put(Actions.setActiveLog())
    let deployData = getDeployConfig();
    if (payload.deploy) {
        deployData = {
            ...deployData,
            ...payload.deploy
        };
    }
    let pathname = yield select((root)=>root.router.pathname);
    let intercept = pathPrefixIntercept(deployData, pathname);
    pathname = intercept.pathname;
    yield putResolve(Actions.setDeployConfig(intercept.deployData));
    let root = yield select();
    let initCacheKeys = selectInitCacheKeys(root);
    // start loading module chunks
    // now is a good time, as Api.initApp will also need some time
    yield fork(loadModuleCode);
    yield fork(loadSagaCode);
    // either empty or auto-detect
    // we need to understand the domain context, but we are not initialized
    // so send current pathname to api/init and let server decode dname
    setCustomVital('XIS');
    let { data: config, error } = yield call(Api.initApp, pathname, initCacheKeys);
    setCustomVital('XID');
    // we need to be ready now
    yield waitForSagaCode();
    yield waitForModuleCode();
    setCustomVital('XCL');
    if (!isSSR) {
        let failures = getCriticalFailures();
        if (size(failures) > 0) {
            for (let { name, error } of failures){
                let sagaName = name || 'unknown';
                log.exception(`critical saga load failed: ${sagaName}`, error);
                yield put(Actions.registerSagaError({
                    code: 'SAGA_IMPORT_FAILED',
                    message: `Critical saga import failed: ${sagaName}`,
                    url: `saga:${sagaName}`
                }));
            }
            yield put(Actions.setAppState({
                state: 'INIT_APP_ERROR'
            }));
            return;
        }
    }
    if (config) {
        // pick sections from response
        let { user, domains, domain: ddata, instance, client, dcss, uistate, widgetSchema, i18n: baseI18n = {} } = config;
        let { name: dname } = ddata;
        ddata = recoveredCachedConfigs(root.config.ddataCache, ddata);
        let instanceWithI18n = {
            ...instance,
            i18n: baseI18n
        };
        ddata = resolveDomainData(ddata, user, instanceWithI18n);
        // derive active user language from key of language pack
        let domainI18n = ddata.i18n || {};
        let language = keys(domainI18n)[0] || keys(baseI18n)[0] || root.meta.language;
        let cfg = {
            domains,
            instance: instanceWithI18n,
            client,
            widgetSchema
        };
        let dcode = yield loadDomainCode(domains, dname);
        if (uistate) {
            yield putResolve(Actions.initUiState(uistate));
        }
        yield putResolve(Actions.setUser({
            user
        }));
        yield putResolve(Actions.setDomainCSS(dcss));
        // update i18n context for current domain in meta
        let mergedI18n = mergeI18nLayers(baseI18n, domainI18n);
        yield putResolve(Actions.setLanguage({
            language,
            dname,
            i18ndata: domainI18n,
            merged: mergedI18n
        }));
        yield putResolve(Actions.setAppConfig({
            config: cfg,
            router: root.router
        }));
        // depends on instance config from setAppConfig
        yield putResolve(Actions.setDomainData(ddata, dcode));
        // server has decided to change our route (probably corrected domain prefix for given id)
        if (config.dispatchedRoute) {
            yield putResolve(Actions.dispatchRoute({
                params: config.dispatchedRoute
            }));
        }
        // we need the domain cfg from above
        root = yield select();
        try {
            var { authNeeded, ...decoded } = decodeRequest(root.router.params, root.router.query, root.config);
        } catch (e) {
            if (e.code == 'E_INVALID_ROUTE') {
                yield put(Actions.setAppState({
                    state: 'INIT_APP_404',
                    router404: root.router
                }));
            } else {
                log.error(e);
                yield put(Actions.setAppState({
                    state: 'INIT_APP_ERROR'
                }));
            }
            return;
        }
        let { runId } = GENERATOR('runId')();
        if (!authNeeded) {
            let runFilters = uix.templating.runFilters;
            if (!runFilters) {
                log.error('runFilters unavailable: templating sagas not loaded');
            } else {
                yield call(runFilters, {
                    ...decoded,
                    tcfg: root.config.templating[dname],
                    runId
                });
            }
        }
        setCustomVital('XAR');
        yield putResolve(Actions.setAppState({
            state: 'APP_RUNNING'
        }));
        yield putResolve(Actions.activateView(runId));
    //~ yield putResolve(Actions.go())
    } else {
        yield put(Actions.setAppState({
            state: 'INIT_APP_ERROR'
        }));
    }
}
export function* fetchDomainInit({ dname }) {
    let { initCacheKeys, ddataCache } = yield getInitCacheKeys(dname);
    // fetch http api
    let resp = yield callDomainInit(dname, initCacheKeys);
    let instance = yield select((root)=>root.config.instance);
    if (resp.data) {
        // normalize response data structure
        return processInitResponse(resp.data, ddataCache, instance);
    } else {
        log.error(resp.error);
        return {};
    }
}
export function* checkAuthState({ user, dname, tcfg_classes }) {
    /* check if user has permissions (acl) for domain/view
   */ let authLost = false;
    let { dname: req_dname, classname, tabname } = yield select(Sel.request);
    if (!user) {
        let { data, error } = yield Api.post(`/${dname || req_dname}/api/userInfo`, {}, {
            timeout: timeouts.DOMAIN_INIT
        }, {
            autoDomain: false
        });
        if (!data) {
            // maybe server not reachable
            log.error(error);
            return authLost // false
            ;
        }
        user = data.user;
    }
    let classcfg = yield select(Sel.classCfg);
    if (tcfg_classes) {
        classcfg = tcfg_classes[classname];
    }
    if (!dname || dname == req_dname) {
        let class_acl_success = !classcfg || !classcfg.acl || checkAcl(classcfg.acl, user, req_dname);
        let tabcfg = classcfg.tabs[tabname];
        let tab_acl_success = !tabcfg || !tabcfg.acl || checkAcl(tabcfg.acl, user, req_dname);
        let appState = yield select((root)=>root.meta.state);
        if (!class_acl_success || !tab_acl_success) {
            log.warn('need more auth for current view');
            if (appState != 'VIEW_UNAUTHORIZED') {
                yield put(Actions.loginFailed(user.username, 'msg_session_expired'));
                yield put(Actions.setAppState({
                    state: 'VIEW_UNAUTHORIZED'
                }));
            }
            authLost = true;
        } else {
            if (appState == 'VIEW_UNAUTHORIZED') {
                yield put(Actions.setAppState({
                    state: 'APP_RUNNING'
                }));
            }
        }
    }
    return authLost;
}
let mergeUser = (user, dname)=>({
        /* resolve domainlevel localgroups into groups
   */ //XXX: we should probably give merged groups a custom name
        groups: uniq([
            ...get(user, `localgroups.${dname}`, []),
            ...user.groups
        ]),
        client: user.client
    });
function* loadModuleCode() {
    modloader.load();
}
function* loadSagaCode() {
    // Non-chunked SSR already registers all sagas up front; avoid double-registering.
    if (isSSR && process.env.UIX_PROD_CHUNKED !== 'true') return;
    sagaloader.load();
}
function* waitForModuleCode() {
    // disabled in SSR (no extra logging of this noop)
    if (!isSSR) {
        let ts = now();
        while(!modloader.isReady()){
            yield delay(5);
        }
        log.info('modules available [%d ms]', now() - ts);
    }
}
function* waitForSagaCode() {
    // disabled in SSR (no extra logging of this noop)
    if (!isSSR) {
        let ts = now();
        while(!sagaloader.isReady()){
            yield delay(5);
        }
        log.info('sagas available [%d ms]', now() - ts);
    }
}
function* loadDomainCode(domains, dname) {
    /* return list of dcode dicts with domain specific rules (exported stuff from domain/index.js)
  - order: self+ancestors
  - excluded: domains without code
  */ // XXX: import() react components with sagas breaks hot-reloading
    // so use this only without react components currently
    let dnames = getAncestorDomains(dname, domains, true).map((d)=>d.name);
    let dcode = yield call(loadCodeForDomains, dnames);
    //~ for (let dname of dnames) {
    //~ if (dcode[dname]) {
    //~ let module = dcode[dname]
    //~ log.x('loaded dcode for %s %o', dname, keys(module))
    //~ }
    //~ }
    return dnames.filter((dname)=>dcode[dname]).map((dname)=>({
            ...dcode[dname],
            dname
        }));
}
function* getInitCacheKeys(dname) {
    let ddataCache = yield select((root)=>root.config.ddataCache);
    let old_ddata = ddataCache[dname];
    let initCacheKeys = old_ddata ? {
        [dname]: old_ddata.cacheKeys
    } : {};
    return {
        initCacheKeys,
        ddataCache
    };
}
function resolveActiveDescendantTypes(ddata, typesById) {
    /* activeDescendantTypes are transported as a bit field - decode integers and map to names
   */ let { activeDescendantTypes } = ddata;
    let byName = dict(mapItems(typesById, (k, v)=>[
            v[-1],
            v
        ]));
    let isActive = (activeSubtypesBits, subtype)=>{
        // check if nth bit is set
        let mask = BigInt(1) << BigInt(subtype);
        return (activeSubtypesBits & mask) !== BigInt(0);
    };
    activeDescendantTypes = dict(mapItems(activeDescendantTypes, (datatypeName, hexbits)=>{
        let activeSubtypesBits = BigInt('0x' + hexbits);
        return [
            datatypeName,
            mapItems(byName[datatypeName], (subtype, type)=>type, (subtype)=>isActive(activeSubtypesBits, subtype))
        ];
    }));
    return {
        ...ddata,
        activeDescendantTypes
    };
}
function processInitResponse(respdata, ddataCache, instance) {
    /* assemble final ddata from response + cache
   */ let { domain: _ddata, user, dcss, name: dname } = respdata;
    _ddata = recoveredCachedConfigs(ddataCache, _ddata);
    let ddata = resolveDomainData(_ddata, user, instance);
    return {
        ddata,
        dcss,
        user
    };
}
function* callDomainInit(dname, initCacheKeys) {
    let resp = yield Api.post(`/${dname}/api/initDomain`, {
        cacheKeys: JSON.stringify(initCacheKeys)
    }, {
        timeout: timeouts.DOMAIN_INIT
    }, {
        autoDomain: false
    });
    return resp;
}
export function* initializeDomain(dname) {
    let { initCacheKeys, ddataCache } = yield getInitCacheKeys(dname);
    let domains = yield select((root)=>root.config.domains);
    if (domains.data[dname]?.isGhost) {
        log.warn('can not initialize ghost domain');
        return null;
    }
    //~ log.info('need domaindata for %o', dname)
    let resp = yield callDomainInit(dname, initCacheKeys);
    let instance = yield select((root)=>root.config.instance);
    if (resp.error) {
        log.error(resp.error);
        return null;
    } else if (resp.data) {
        let { ddata, user, dcss } = processInitResponse(resp.data, ddataCache, instance);
        // XXX: broken since switch to views.js templating
        // we need probably new strategy for entering a non-auth domain
        //~ let authLost = yield checkAuthState({
        //~ user,
        //~ dname: ddata.name,
        //~ tcfg_classes: ddata.templating.classes,
        //~ })
        let dcode = yield loadDomainCode(domains, dname);
        // if we would loose auth on current view, do not set ddata
        // checkAuthState should handle further steps now
        //~ if (!authLost) {
        yield putResolve(Actions.setDomainCSS(dcss));
        yield putResolve(Actions.setDomainData(ddata, dcode));
        //~ }
        return ddata;
    } else {
        log.warn('neither data nor error, domain init failed');
    }
}
function* _initDomain({ payload }) {
    yield initializeDomain(payload.dname);
}
function* setSessionVar({ payload }) {
    let { name, value } = payload;
    let { data, error, cancelled } = yield Api.get('/api/setSessionVar', {
        name,
        value
    });
    let success = !error && !cancelled;
    if (!success) log.warn('failed to set session var %o', payload);
}
function* restartApp({ payload }) {
    if (payload?.sessionVar) {
        yield call(setSessionVar, {
            payload: payload.sessionVar
        });
    }
    if (process.env.UIX_ENV == 'browser') {
        window.location.href = window.location.href;
    } else {
        throw Error(`unsupported env ${process.env.UIX_ENV}`);
    }
}
function* _handleErrorNotification({ error }) {
    if (error) {
        yield put(Actions.errorNotification({
            error,
            header: 'API-Error'
        }));
    }
}
function* _callApi({ payload }) {
    /* generic api call with callback function
  this transports functions through action system
  and should only be used for non-complex use cases
  */ let { name, params, callback, options } = payload;
    let { method, cancelToken, disableErrorNotify, ...moreOptions } = options;
    let f = Api[options.method || 'get'];
    let axopts = {
        timeout: options.timeout || timeouts.API_DEFAULT
    };
    if (cancelToken) {
        axopts.cancelToken = cancelToken;
    }
    if (name.startsWith('http://') || name.startsWith('https://')) {
        // non vls-api endpont
        var url = name;
        // we do not want to send cookies (withCredentials)
        // and we dont assume CORS support for headers
        axopts = {
            ...axopts,
            withCredentials: false,
            withHeaders: false
        };
    } else if (name.startsWith('/')) {
        // like /download/zcmets/..
        var url = name;
        // raw will only return data.responseText
        moreOptions = {
            ...moreOptions,
            autoDomain: false,
            raw: true
        };
    } else {
        var url = `/api/${name}`;
        moreOptions = {
            ...moreOptions,
            autoDomain: true
        };
    }
    // XXX: unclear if good idea: strip undefineds - aligns with clicks in debug footer
    params = dict(mapItems(params, (k, v)=>[
            k,
            v === undefined ? '' : v
        ]));
    let { data, error } = yield f(url, params, axopts, moreOptions);
    if (!disableErrorNotify) {
        yield _handleErrorNotification({
            error
        });
    }
    if (callback) {
        // run callback
        try {
            callback(data, error);
        } catch (e) {
            log.exception(e);
        }
    }
    return {
        data,
        error
    };
}
function* _getApi({ payload, meta }) {
    /* fetch data for useApi hook
   */ let { name, params, key, options } = payload;
    let { method, cancelToken, disableErrorNotify, ...moreOptions } = options;
    let f = Api[method || 'get'];
    let axopts = {
        timeout: options.timeout || timeouts.API_DEFAULT
    };
    if (cancelToken) {
        axopts.cancelToken = cancelToken;
    }
    if (name.startsWith('/')) {
        // like /download/zcmets/..
        var url = name;
        // raw will only return data.responseText
        moreOptions = {
            ...moreOptions,
            autoDomain: false,
            raw: true
        };
    } else {
        var url = `/api/${name}`;
        moreOptions = {
            ...moreOptions,
            autoDomain: true
        };
    }
    let runId = yield select(Sel.runId);
    let result = yield f(url, params, axopts, moreOptions);
    if (!disableErrorNotify) {
        yield _handleErrorNotification(result);
    }
    yield put(Actions.setApi(key, result, meta.apiId, runId));
}
function* _reduceApi({ payload }) {
    /* fancy generic api call with callback reducer function
  this transports functions through action system
  and should only be used for non-complex use cases
  */ let { reducer, ...otherPayload } = payload;
    if (!reducer) {
        // hmm, this indirect throw is only for testReducerError
        reducer = (dataState, apiResult)=>{
            throw new Error('no reducer function given');
        };
    }
    let { name } = otherPayload;
    let { data, error } = yield _callApi({
        payload: otherPayload
    });
    yield put(Actions.reduceApiResult(data, error, reducer, name));
}
function* watchReduceApi(numWorkers) {
    const chan = yield actionChannel(Actions.reduceApi, buffers.expanding(100));
    for (let i of range(numWorkers)){
        // spawn versus fork: sagas do not terminate on END in ssr env
        // so use spawn
        yield spawn(function*() {
            while(true){
                let action = yield take(chan);
                yield _reduceApi(action);
            }
        });
    }
}
function* runExpiration() {
    /* we could handle this more gracefully by preserving the current view
  and using a overlay dialog for re-auth
  */ let loginActive = yield select((root)=>!root.meta.user.isGuest);
    if (loginActive) {
        let username = yield select((root)=>root.meta.user.name);
    //~ yield put(Actions.loginFailed(username, 'msg_session_expired'))
    //~ yield put(Actions.runLogout('session expired'))
    }
}
let INIT_APP = ({ type, payload })=>type == 'SET_APP_STATE' && payload.state == 'INIT_APP';
let REHYDRATE_CONFIG = ({ type, key })=>type == 'persist/REHYDRATE' && key == 'uix_config';
function* reportException({ payload }) {
    let [logmsg, ...more] = payload.args;
    let errobj = more.filter((arg)=>arg instanceof Error)[0] || null;
    if (errobj) {
        let errorMessage = errobj.toString().split('\n')[0];
        errobj = {
            message: errorMessage,
            stack: errobj.stack
        };
    }
    let args = more.filter((arg)=>!(arg instanceof Error));
    let root = yield select((root)=>root);
    let data = {
        type: 'Exception',
        error: JSON.stringify({
            logmsg,
            args,
            ...errobj
        }),
        ...getErrorEnvDetails(root)
    };
    yield Api.post('/api/reportError', data);
}
function* watchLoggedExceptions() {
    while(true){
        let { theEnd } = yield race({
            waitFor: delay(500),
            theEnd: take('@@redux-saga/CHANNEL_END')
        });
        if (theEnd) break;
        let exceptions = [
            ...loggedExceptions
        ];
        loggedExceptions.length = 0;
        for (let args of exceptions){
            yield reportException({
                payload: {
                    args
                }
            });
        }
    }
}
function* reportErrorBoundary({ payload }) {
    let { otid, errorMessage, errorStack, componentStack } = payload;
    let root = yield select((root)=>root);
    let data = {
        type: 'ErrorBoundary',
        error: JSON.stringify({
            message: errorMessage,
            stack: errorStack,
            componentStack
        }),
        ...getErrorEnvDetails(root)
    };
    if (otid) {
        data = {
            ...data,
            otid
        };
    }
    yield Api.post('/api/reportError', data);
}
function* replaceQueryParams({ payload }) {
    /* only update browser location bar - no further effects
  - with custom handling of an 'id' parameter (replaces last part of pathname)
  */ if (!window || !window.history) return;
    let { id, ...params } = payload.params;
    let request = yield select(Sel.request);
    let { id: replaced_id, ...replacedQueryParams } = yield select((root)=>root.meta.replacedQueryParams);
    let pathPrefix = yield select((root)=>root.config.deploy.pathPrefix);
    params = {
        ...request.query,
        ...replacedQueryParams,
        ...params
    };
    params = dict(filterItems(params, (k, v)=>v !== null));
    let qs = queryString.stringify(params);
    let p = request.pathname;
    if (id) {
        // replace last part with id
        if (request.id) {
            p = [
                ...p.split('/').slice(0, -1),
                id
            ].join('/');
        } else {
            // no id part needs to be removed
            p = [
                ...p.split('/'),
                id
            ].join('/');
        }
    }
    p = ensurePathPrefix(pathPrefix, p);
    window.history.replaceState({}, '', `${p}${qs ? '?' : ''}${qs}`);
}
export function* rootSaga() {
    /* build the root saga by combining all module level sagas
   */ log.saga('run root saga');
    // trigger on actions, the key is visible in sagamon logging
    let sagas = {
        onRehydration: takeEvery(REHYDRATE_CONFIG, rehydrateApp),
        initializeApp: takeEvery(INIT_APP, initializeApp),
        reportException: takeEvery('REPORT_EXCEPTION', reportException),
        watchLoggedExceptions: watchLoggedExceptions(),
        reportErrorBoundary: takeEvery('REPORT_ERROR_BOUNDARY', reportErrorBoundary),
        setSessionVar: takeEvery(Actions.setSessionVar, setSessionVar),
        replaceQueryParams: takeEvery(Actions.replaceQueryParams, replaceQueryParams),
        callApi: takeEvery(Actions.callApi, _callApi),
        getApi: takeEvery(Actions.getApi, _getApi),
        //~ reduceApi: takeEvery(Actions.reduceApi, _reduceApi),
        watchReduceApi: watchReduceApi(4),
        restartApp: takeEvery(Actions.restartApp, restartApp),
        // loopers moved to sagas_extra
        // from module registration
        ...Sagas
    };
    if (isSSR) {
        sagas.watchInitDomainSSR = watchInitDomainSSR();
    }
    yield all(sagas);
}
